<script lang="ts" setup>
import { computed, toRefs, useSlots } from "vue";

const slots = useSlots();

type ButtonType = "button" | "route-link" | "external-link";
type ButtonVariant = "primary" | "secondary" | "neutral" | "text" | "icon";
type ButtonSize = "small" | "big" | "dense" | "xs";
type Alignment = "left" | "center" | "right";

const props = withDefaults(
  defineProps<{
    type?: ButtonType;
    variant?: ButtonVariant;
    size?: ButtonSize;
    disabled?: boolean;
    fullWidth?: boolean;
    notUpper?: boolean;
    to?: string;
    align?: Alignment;
    noLabel?: boolean;
  }>(),
  {
    noLabel: false,
    align: "center",
    variant: "neutral",
    to: "",
    size: "big",
    type: "button",
    disabled: false,
    notUpper: false,
    fullWidth: false,
  }
);
const {
  to,
  noLabel,
  size,
  type,
  disabled,
  fullWidth,
  notUpper,
  variant,
  align,
} = toRefs(props);

const rootClasses = computed(() => [
  "common",
  variant.value,
  size.value,
  align.value,
  {
    link: type.value === "external-link" || type.value === "route-link",
    "full-width": fullWidth.value,
    upper: !notUpper.value,
  },
]);

const component = computed(() => {
  if (type.value === "button") {
    return "button";
  }

  if (type.value === "route-link") {
    return "router-link";
  }

  return "a";
});

const emit = defineEmits(["click"]);
const onClick = (e: MouseEvent) => {
  if (disabled.value) {
    e.stopImmediatePropagation();
  } else if (type.value === "external-link") {
    window.open(to.value, "_blank");
  } else {
    emit("click");
  }
};
</script>

<template>
  <div :class="rootClasses" :disabled="disabled" @click="onClick">
    <component :is="component" :to="to" :disabled="disabled" class="button">
      <slot name="prepend" />
      <div v-if="!noLabel" class="content">
        <slot />
      </div>
    </component>
  </div>
</template>

<style lang="scss" scoped>
.button {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  box-sizing: border-box;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;

  &:not(:disabled) {
    cursor: pointer;
  }

  .content {
    font-family: Roboto, serif;
  }
}

.common {
  display: inline-block;
  box-sizing: border-box;

  .button {
    @include row(14px);
    align-items: center;
    border: none;
    box-sizing: border-box;
    font-weight: 500;
    font-size: $ui-font-size;

    &:not(:disabled) {
      cursor: pointer;
    }
  }
}

.primary {
  .button {
    background: $ui-accent;
    border-radius: $border-radius;
    color: $ui-white;

    &:hover {
      // background: lighten($color: $ui-accent, $amount: 5%); --- https://sass-lang.com/documentation/breaking-changes/color-functions/
    }

    &:active {
      // background: lighten($color: $ui-accent, $amount: 10%); --- https://sass-lang.com/documentation/breaking-changes/color-functions/
    }

    &:disabled {
      background: $ui-bg-disabled;
      color: $ui-disabled;

      :deep(svg) {
        path {
          fill: $ui-disabled;
        }
      }
    }
  }
}

// TODO: Add 'secondary' and 'neutral' classes
.text {
  .button {
    background: transparent;

    color: $ui-accent;

    &:hover {
      // color: lighten($color: $ui-accent, $amount: 5%); --- https://sass-lang.com/documentation/breaking-changes/color-functions/
    }

    &:active {
      // color: lighten($color: $ui-accent, $amount: 10%); --- https://sass-lang.com/documentation/breaking-changes/color-functions/
    }

    &:disabled {
      color: $ui-disabled;
    }
  }
}

.neutral {
  .button {
    background: $ui-background-accent-light;
    border-radius: $border-radius;
    color: $ui-accent;

    &:disabled {
      background: $ui-bg-disabled;
      color: $ui-disabled;
    }
  }
}

.icon {
  width: 36px;
  height: 36px;
  .button {
    background: gray;

    border-radius: 17px;

    &:disabled {
      background: $ui-bg-disabled;
      color: $ui-disabled;
    }
  }
}

.upper {
  .button {
    text-transform: uppercase;
  }
}

.full-width {
  width: 100%;

  button {
    width: 100%;
  }
}

.xs {
  .button {
    padding: 8px 24px;
  }
}

.small {
  .button {
    padding: 8px 24px;
    height: 40px;
  }
}

.big {
  .button {
    padding: 10px 24px;
    height: 44px;
  }
}

.dense {
  .button {
    padding: 0;
    height: auto;
  }
}

.link {
  text-align: center;

  .button {
    text-decoration: none;
    color: $ui-accent;
    -webkit-appearance: initial;
  }
}

.left {
  .button {
    justify-content: flex-start;
  }
}

.center {
  .button {
    justify-content: space-around;
  }
}
.right {
  .button {
    justify-content: flex-end;
  }
}
</style>
